// src/components/DashboardDesktop.jsx
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Box, Paper, Typography, Chip, Stack, Button, Card, CardContent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  getCountFromServer,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import { useIsPremium } from "../hooks/useIsPremium";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import MetricCard from "./MetricCard";
import AgentsTable from "./AgentsTable";
import { PLAN_LIMITS } from "../constants/planLimits";

export default function DashboardDesktop() {
  const theme = useTheme();
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();
  const { isPremium } = useIsPremium();
  const navigate = useNavigate();
  const planKey = isPremium ? "premium" : "free";
  const planLimits = PLAN_LIMITS[planKey];
  const basicLimits = PLAN_LIMITS.free;
  const premiumLimits = PLAN_LIMITS.premium;

  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState("owner");

  const [agents, setAgents] = useState([]);
  const [totalConversations, setTotalConversations] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [agentTokenMap, setAgentTokenMap] = useState({});

  // 1) Profil utilisateur → tenantId + dark mode
  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setProfile({ id: snap.id, ...data });
      setRole(data.role ?? "owner");

      if (data.darkMode !== undefined) {
        window.dispatchEvent(
          new CustomEvent("darkmode:changed", { detail: data.darkMode })
        );
      }
    });
    return () => unsub();
  }, [currentUser]);

  // 2) Agents
  useEffect(() => {
    if (!profile?.tenantId) return;
    const agentsRef = collection(db, "tenants", profile.tenantId, "agents");
    const unsubAgents = onSnapshot(
      query(agentsRef, orderBy("createdAt", "desc")),
      (snap) => setAgents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => {
      unsubAgents();
    };
  }, [profile?.tenantId]);

  // 3) Total conversations across all agents
  useEffect(() => {
    if (!agents.length) {
      setTotalConversations(0);
      return;
    }
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const counts = await Promise.all(
          agents.map(async (agent) => {
            if (!agent?.id) return 0;
            try {
              const convRef = collection(
                db,
                "messages",
                `agent_${agent.id}`,
                "conversations"
              );
              const snapshot = await getCountFromServer(convRef);
              return snapshot.data().count || 0;
            } catch (err) {
              console.error("Failed to count conversations for", agent.id, err);
              return 0;
            }
          })
        );
        if (!cancelled) {
          setTotalConversations(counts.reduce((sum, c) => sum + c, 0));
        }
      } catch (error) {
        console.error("Failed to load conversations count", error);
        if (!cancelled) setTotalConversations(0);
      }
    };
    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [agents]);

  // 4) Total tokens usage across all agents
  useEffect(() => {
    if (!profile?.tenantId || !agents.length) {
      setTotalTokens(0);
      setAgentTokenMap({});
      return;
    }
    let cancelled = false;
    const fetchTokens = async () => {
      try {
        const perAgent = await Promise.all(
          agents.map(async (agent) => {
            if (!agent?.id) return { id: agent?.id, tokens: 0 };
            try {
              const usageRef = collection(
                db,
                "tenants",
                profile.tenantId,
                "agents",
                agent.id,
                "usage_daily"
              );
              const snap = await getDocs(usageRef);
              let total = 0;
              snap.forEach((docSnap) => {
                total += Number(docSnap.data()?.totalTokens || 0) || 0;
              });
              return { id: agent.id, tokens: total };
            } catch (err) {
              console.error("Failed to aggregate tokens for", agent.id, err);
              return { id: agent.id, tokens: 0 };
            }
          })
        );
        if (!cancelled) {
          const total = perAgent.reduce((sum, item) => sum + item.tokens, 0);
          setTotalTokens(total);
          const map = {};
          perAgent.forEach((item) => {
            if (item?.id) map[item.id] = item.tokens;
          });
          setAgentTokenMap(map);
        }
      } catch (error) {
        console.error("Failed to compute total tokens", error);
        if (!cancelled) {
          setTotalTokens(0);
          setAgentTokenMap({});
        }
      }
    };
    fetchTokens();
    return () => {
      cancelled = true;
    };
  }, [agents, profile?.tenantId]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);

  const formatShort = useCallback((value) => {
    if (value >= 1_000_000) return `${value / 1_000_000}M`;
    if (value >= 1_000) return `${value / 1_000}k`;
    return `${value}`;
  }, []);

  const metrics = useMemo(() => {
    return [
      {
        title: translate?.("Agents") || "Agents",
        value: agents.length,
        color: theme.palette.mode === "dark" ? "#7aa2f7" : "#3b82f6",
        suffix: ` / ${planLimits.maxAgents}`,
        helperLines: [
          {
            text: isPremium
              ? translate?.("PlanPremiumAgents") || "Premium: 15 agents"
              : translate?.("PlanBasicAgents") || "Basic: 3 agents",
          },
          !isPremium
            ? {
                text: translate?.("PlanUpgradeCTA") || "Go Premium",
                actionLabel: translate?.("PlanUpgradeCTA") || "Go Premium",
                onClick: () => navigate("/app/premium"),
              }
            : null,
        ].filter(Boolean),
      },
      {
        title: translate?.("Conversations") || "Conversations",
        value: totalConversations,
        color: theme.palette.mode === "dark" ? "#f7768e" : "#ef4444",
      },
      {
        title: translate?.("TotalTokensUsed") || "Tokens used",
        value: Math.round(totalTokens),
        color: theme.palette.mode === "dark" ? "#c084fc" : "#8b5cf6",
        formatter: (val) => numberFormatter.format(val),
        suffix: ` / ${formatShort(planLimits.monthlyTokens)}`,
        animationDuration: 200,
        helperLines: [
          {
            text: isPremium
              ? translate?.("PlanPremiumTokens") || "Premium: 2M tokens"
              : translate?.("PlanBasicTokens") || "Basic: 200k tokens",
          },
          !isPremium
            ? {
                text: translate?.("PlanUpgradeCTA") || "Go Premium",
                actionLabel: translate?.("PlanUpgradeCTA") || "Go Premium",
                onClick: () => navigate("/app/premium"),
              }
            : null,
        ].filter(Boolean),
      },
    ];
  }, [
    agents.length,
    totalConversations,
    totalTokens,
    theme.palette.mode,
    translate,
    numberFormatter,
    formatShort,
    planLimits,
    basicLimits,
    premiumLimits,
    isPremium,
    navigate,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />

      <Box sx={{ width: "90%", boxSizing: "border-box", ml: "5px" }}>
        <HeaderBar
          title={translate?.("Dashboard") || "Dashboard"}
          subtitle={
            translate?.("WelcomeToYourAIPlatform") ||
            "Overview of your AI agents and usage"
          }
          showProjectSelect={false}
          userName={currentUser?.displayName}
          userRole={role}
        />

        {/* Metrics */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            px: 3,
            py: 2,
            pt: "30px",
            bgcolor: "background.default",
          }}
        >
          {metrics.map((m) => (
            <Box key={m.title} sx={{ flex: "0 0 240px" }}>
              <MetricCard
                title={m.title}
                value={m.value}
                unit={m.unit || ""}
                color={m.color}
                animationDuration={m.animationDuration}
                formatter={m.formatter}
                suffix={m.suffix}
                helperLines={m.helperLines}
              />
            </Box>
          ))}
          <Box sx={{ flex: "0 0 240px" }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {translate?.("CurrentPlanLabel") || "Your current plan"}
                </Typography>
                <Chip
                  label={
                    isPremium
                      ? translate?.("PlanPremiumLabel") || "Premium"
                      : translate?.("PlanFreeLabel") || "Basic"
                  }
                  color={isPremium ? "warning" : "default"}
                  sx={{
                    mt: 1,
                    color: isPremium ? "black" : "inherit",
                    fontWeight: 600,
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {isPremium
                    ? translate?.("PlanDetailsPremium") ||
                      "Premium: 15 agents / 2M tokens per month."
                    : translate?.("PlanDetailsBasic") ||
                      "Basic: 3 agents / 200k tokens per month."}
                </Typography>
                {!isPremium && (
                  <Button
                    variant="text"
                    onClick={() => navigate("/app/premium")}
                    sx={{ mt: 1, textTransform: "none", px: 0 }}
                  >
                    {translate?.("PlanUpgradeCTA") || "Go Premium"}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Recent Agents */}
        <Box sx={{ px: 3, pb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant="h6">
                {translate("RecentAgents") || "Recent agents"}
              </Typography>
            </Box>
            <AgentsTable
              rows={agents}
              emptyLabel={translate?.("NoAgentsYet") || "No agents yet"}
              tokenByAgent={agentTokenMap}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
